import { useEffect, useMemo, useRef, useState } from 'react'
import { useLayoutContext } from '../../contexts/LayoutContext'
import styles from './QuestionInput.module.css'
import { useAuth } from '../../hooks/useAuth'
import { useConfig } from '../../contexts/ConfigContext'
import ButtonPanel from '../ButtonPanel/ButtonPanel'
import type { MicrophoneHandle } from '../Microphone/micprototype'
import { InfoLabel } from '../../components/InfoLabel/InfoLabel'
import DOMPurify from 'dompurify'

// Hoisted: a fresh options object per render defeats any memo, and this now runs
// on a hot path — live dictation calls setQuestion for every interim result.
const SANITIZE_OPTS = {
    FORBID_TAGS: [
        'style',
        'img',
        'src',
        'a',
        'h1',
        'p',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'div',
    ],
}

interface Props {
    onSend: (question: string) => void
    disabled: boolean
    initQuestion?: string
    placeholder?: string
    clearOnSend?: boolean
    DisplayText?: string
    micPermissionsDenied: boolean
}

interface QuestionInputOutletContext {
    isMobile: boolean
}

export const QuestionInput = ({
    onSend,
    disabled,
    placeholder,
    clearOnSend,
    initQuestion,
    micPermissionsDenied,
}: Props) => {
    const [question, setQuestion] = useState<string>('')
    const [isRecording, setIsRecording] = useState(false)
    const [isTranscribing, setIsTranscribing] = useState<boolean>(false)
    const [isKeyboardOpen, setIsKeyboardOpen] = useState<boolean>(false)
    const [sendingTranscription, setSendingTranscription] =
        useState<boolean>(false)

    const micRef = useRef<MicrophoneHandle | null>(null)

    // Memoised: sanitising parses the string into a DOM tree, and dictation drives
    // `question` at up to ~50 updates/sec over text that grows to ~25 KB across a
    // long contribution. Unmemoised this re-parsed the whole transcript on every
    // unrelated re-render too.
    const escapedContent = useMemo(
        () => DOMPurify.sanitize(question, SANITIZE_OPTS),
        [question]
    )
    const { isAuthenticated, isAnonymous } = useAuth()
    const { requiresAuth, anonymousPageAccess } = useConfig()

    const { isMobile } = useLayoutContext()

    useEffect(() => {
        initQuestion && setQuestion(initQuestion)
    }, [initQuestion])

    const sendQuestion = () => {
        if (disabled || !escapedContent.trim()) {
            return
        }

        onSend(escapedContent)

        if (clearOnSend) {
            setQuestion('')
        }
    }

    const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
        if (ev.key === 'Enter' && !ev.shiftKey) {
            ev.preventDefault()
            sendQuestion()
        }
    }

    const onQuestionChange = (ev: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = ev.target.value
        if (!newValue) {
            setQuestion('')
        } else if (newValue.length <= 10000) {
            setQuestion(newValue)
        }
    }

    // new function for the microphone to help retain transcription
    const appendTranscription = (text: string) => {
        setQuestion((prevQuestion) => {
            const newValue = prevQuestion + ' ' + text
            return newValue.length <= 10000 ? newValue : prevQuestion
        })
    }

    const handleCancel = () => {
        micRef.current?.cancelRecording() // calls child's method
        setIsTranscribing(false)
        setIsRecording(false)
    }

    const canAsk =
        isAuthenticated ||
        (!requiresAuth && isAnonymous && anonymousPageAccess.chat)
    const disableRequiredAccessControl = !canAsk
    const sendQuestionDisabled =
        disabled || !escapedContent.trim() || disableRequiredAccessControl

    if (disableRequiredAccessControl) {
        placeholder = 'Please login to continue...'
    }

    let placeholderText = isTranscribing
        ? 'Transcribing...'
        : sendingTranscription
          ? 'Sending transcription...'
          : placeholder

    return (
        <div
            className={
                isKeyboardOpen
                    ? styles.inputBackgroundContainerKeyboardOpen
                    : styles.inputBackgroundContainer
            }
        >
            <div className={styles.questionInputContainer}>
                <textarea
                    className={styles.questionInputTextArea}
                    disabled={disableRequiredAccessControl}
                    placeholder={placeholderText}
                    value={question}
                    onChange={onQuestionChange}
                    onKeyDown={onEnterPress}
                    style={{
                        height: '7.5rem',
                        width: '100%',
                        padding: '0.5rem',
                        backgroundColor: 'transparent',
                    }}
                />
                <div className={styles.questionInputButtonsContainer}>
                    <ButtonPanel
                        updateQuestion={setQuestion}
                        setIsTranscribing={setIsTranscribing}
                        ref={micRef}
                        isRecording={isRecording}
                        setIsRecording={setIsRecording}
                        chatType="ask"
                        sendQuestion={sendQuestion}
                        sendQuestionDisabled={sendQuestionDisabled}
                        handleCancel={handleCancel}
                        setSendingTranscription={setSendingTranscription}
                        micPermissionsDenied={micPermissionsDenied}
                        appendTranscription={appendTranscription}
                    />
                </div>
            </div>
            <div
                className={
                    isKeyboardOpen ? styles.clauseTextHidden : styles.clauseText
                }
            >
                <InfoLabel
                    info="Suggestions given by Lorefully are automatically generated by an AI system and should be used for informational purposes only. Although all suggestions are intended to help you, we can't guarantee the accuracy, completeness, or timeliness of the information provided. Any actions you take based on Lorefully content are at your own risk, so we recommend seeking qualified expertise or carrying-out further research to validate and supplement any information given, just to make sure."
                    label={
                        <>
                            Suggestions given by Lorefully should be used for{' '}
                            <br
                                style={
                                    isMobile
                                        ? { display: 'block' }
                                        : { display: 'none' }
                                }
                            />
                            informational purposes only.
                        </>
                    }
                    className={styles.infoLabel}
                    iconSize={14}
                />
            </div>
        </div>
    )
}
